// IE Hub - Onboarding Page
// Multi-step onboarding flow with form collection and illustration

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Container,
  Divider,
  Drawer,
  IconButton,
  Link,
  Paper,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText
} from '../../ui';
import { CloseIcon } from '../../ui';
import { TextField, UIButton } from '../../ui';
import { useHelp } from '@/shared/help/HelpProvider';
import InfoDrawer from '../../components/InfoDrawer';
// Slide-up error UI will be handled via Drawer, not modal dialog
import { infoContentSchema } from '../../shared/types/infoContent';
import termsJson from '../../knowledge/terms.json';
import privacyJson from '../../knowledge/privacy.json';
import { AuthService } from '../../shared/api/services/authService';
import { useUI } from '../../shared/state/store';
import { useAlert } from '../../app/providers/AlertProvider';
import { useAuth } from '../../shared/state/store';
import { useLocation, useNavigate } from 'react-router-dom';
import { getOnboardingRedirect } from '../../shared/lib/getOnboardingRedirect';
import { isMagicLinkError } from '../../shared/types/auth';
import SimpleLoadingScreen from '../../components/SimpleLoadingScreen';
import PoweredByIEHub from '@/components/PoweredByIEHub';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { getStoredCtTokenIfValid, persistCtIfValid } from '@/shared/prearrears/ctToken';
import { trackMetaEvent } from '@/shared/tracking/metaTracking';

interface AuthScreenData {
  email: string;
}

type DialogType = 'login' | 'signup';

// Stricter email validation function
const isValidEmail = (email: string): boolean => {
  const trimmed = email.trim();

  // Basic structure check: must have exactly one @
  if (!trimmed.includes('@') || trimmed.split('@').length !== 2) {
    return false;
  }

  const [localPart, domainPart] = trimmed.split('@');

  // Local part validation (before @)
  // Must not be empty
  if (!localPart || localPart.length === 0) {
    return false;
  }

  // Must not start or end with a dot
  if (localPart.startsWith('.') || localPart.endsWith('.')) {
    return false;
  }

  // Must not have consecutive dots
  if (localPart.includes('..')) {
    return false;
  }

  // Local part can contain: letters, numbers, and these special chars: . ! # $ % & ' * + / = ? ^ _ ` { | } ~ -
  // But we'll use a regex to validate the allowed characters
  const localPartRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+$/;
  if (!localPartRegex.test(localPart)) {
    return false;
  }

  // Domain part validation (after @)
  // Must not be empty
  if (!domainPart || domainPart.length === 0) {
    return false;
  }

  // Must not start or end with a dot or hyphen
  if (domainPart.startsWith('.') || domainPart.endsWith('.') ||
    domainPart.startsWith('-') || domainPart.endsWith('-')) {
    return false;
  }

  // Must not have consecutive dots
  if (domainPart.includes('..')) {
    return false;
  }

  // Must have at least one dot (for TLD)
  if (!domainPart.includes('.')) {
    return false;
  }

  // Split domain into parts by dots
  const domainParts = domainPart.split('.').filter(Boolean);

  // Each domain part must:
  // - Not be empty
  // - Not start or end with hyphen
  // - Contain only letters, numbers, and hyphens
  for (const part of domainParts) {
    if (part.length === 0) {
      return false; // Empty part (consecutive dots already caught above, but double-check)
    }
    if (part.startsWith('-') || part.endsWith('-')) {
      return false;
    }
    // Domain part can only contain letters, numbers, and hyphens
    const domainPartRegex = /^[a-zA-Z0-9-]+$/;
    if (!domainPartRegex.test(part)) {
      return false;
    }
  }

  // TLD (last part) must be at least 2 characters and only letters
  const tld = domainParts[domainParts.length - 1] ?? '';
  if (tld.length < 2) {
    return false;
  }
  const tldRegex = /^[a-zA-Z]+$/;
  if (!tldRegex.test(tld)) {
    return false;
  }

  // Overall length check (RFC 5321: local part max 64, domain max 255)
  if (localPart.length > 64 || domainPart.length > 255) {
    return false;
  }

  return true;
};

const getEmailError = (email: string): string => {
  if (!email.trim()) {
    return 'Please enter your email address';
  }
  if (!isValidEmail(email)) {
    return 'Please enter a valid email address';
  }
  return '';
};

// Helper function to format timer display
const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes > 0) {
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }
  return `${remainingSeconds}s`;
};

// Extract cooldown seconds from backend message like "Please try again after 14 minutes"
const extractCooldownSeconds = (message: string): number => {
  const match = message.match(/(\d+)\s*minute/i);
  const minutesStr = match?.[1];
  if (typeof minutesStr === 'string' && minutesStr.length > 0) {
    const minutes = Number.parseInt(minutesStr, 10);
    if (!Number.isNaN(minutes) && minutes > 0) {
      return minutes * 60;
    }
  }
  return 900; // default to 15 minutes if not specified
};

// Helper function to get the appropriate image and text based on state
const getEmailSentContent = (resendAttempts: number, isRateLimited: boolean) => {
  if (isRateLimited) {
    return {
      image: '/assets/onBoarding/TooMany.svg',
      title: 'Too Many Attempts',
      description: 'You\'ve reached the maximum number of resend attempts.',
      instruction: 'Please try again after 15 minutes.'
    };
  } else if (resendAttempts > 0) {
    return {
      image: '/assets/onBoarding/EmailResent.svg',
      title: 'Link sent again',
      description: 'We’ve just sent a fresh link to',
      instruction: 'Open the link to continue',
      moreDescription: 'Please be sure to ',
      moreDescriptionBold: 'Check Spam/Junk folder'
    };
  } else {
    return {
      image: '/assets/onBoarding/EmailSent.svg',
      title: 'Check your inbox',
      description: 'We\'ve sent a secure sign-in link to',
      moreDescription: 'Please be sure to check your spam folder',
      instruction: 'Open the link to access your IE Hub account.'
    };
  }
};

// Carousel images
const carouselImages = [
  '/assets/onBoarding/crausal1.webp',
  '/assets/onBoarding/crausal2.webp',
  '/assets/onBoarding/crausal3.webp',
  '/assets/onBoarding/crausal4.webp'
];

export default function AuthScreen(): JSX.Element {
  const [formData, setFormData] = useState<AuthScreenData>({
    email: ''
  });

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const goToNextImage = (): void => {
    setCurrentImageIndex((prev) => (prev + 1) % carouselImages.length);
  };
  const goToPrevImage = (): void => {
    setCurrentImageIndex((prev) => (prev - 1 + carouselImages.length) % carouselImages.length);
  };

  // Touch swipe handling for carousel
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const isSwipingRef = useRef<boolean>(false);
  const lastDxRef = useRef<number>(0);

  const onTouchStartCarousel = (event: React.TouchEvent<HTMLDivElement>): void => {
    if (event.touches.length === 0) return;
    const t = event.touches.item(0);
    if (!t) return;
    touchStartRef.current = { x: t.clientX, y: t.clientY };
    isSwipingRef.current = false;
    lastDxRef.current = 0;
  };

  const onTouchMoveCarousel = (event: React.TouchEvent<HTMLDivElement>): void => {
    const start = touchStartRef.current;
    if (!start) return;
    if (event.touches.length === 0) return;
    const t = event.touches.item(0);
    if (!t) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (!isSwipingRef.current) {
      if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) {
        isSwipingRef.current = true;
      } else {
        return;
      }
    }
    // When swiping horizontally, prevent vertical scroll from hijacking the gesture
    event.preventDefault();
    lastDxRef.current = dx;
  };

  const onTouchEndCarousel = (_event: React.TouchEvent<HTMLDivElement>): void => {
    if (isSwipingRef.current) {
      const dx = lastDxRef.current;
      const threshold = 50;
      if (dx <= -threshold) {
        goToNextImage();
      } else if (dx >= threshold) {
        goToPrevImage();
      }
    }
    touchStartRef.current = null;
    isSwipingRef.current = false;
    lastDxRef.current = 0;
  };
  // Global help via provider
  const [showLoginOptions, setShowLoginOptions] = useState(true);
  const [loginBottomSheetOpen, setLoginBottomSheetOpen] = useState(false);
  const [dialogType, setDialogType] = useState<DialogType>('login');
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [emailError, setEmailError] = useState<string>('');
  const [emailTouched, setEmailTouched] = useState<boolean>(false);
  const [resendAttempts, setResendAttempts] = useState<number>(0);
  const [showResendOption, setShowResendOption] = useState<boolean>(false);
  const [resendTimer, setResendTimer] = useState<number>(0);
  const [isRateLimited, setIsRateLimited] = useState<boolean>(false);
  const [rateLimitTimer, setRateLimitTimer] = useState<number>(0);
  const [rateLimitMessage, setRateLimitMessage] = useState<string>('');
  const [termsOpen, setTermsOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [mailChooserOpen, setMailChooserOpen] = useState(false);
  const [linkExpired, setLinkExpired] = useState(false);
  const [verifyError, setVerifyError] = useState<{ message: string; description?: string } | null>(null);
  const [errorSheetOpen, setErrorSheetOpen] = useState<boolean>(false);
  // Force-remount key for iOS Safari repaint issues on Drawer content switches
  const [contentEpoch, setContentEpoch] = useState<number>(0);

  // Terms and privacy use the same structured format
  const termsContent = useMemo(() => infoContentSchema.parse(termsJson), []);
  const privacyContent = useMemo(() => infoContentSchema.parse(privacyJson), []);

  const { setLoading, loading } = useUI();
  const { showError, showSuccess, showWarning } = useAlert();
  const location = useLocation();
  const navigate = useNavigate();
  const { completeAuth } = useAuth();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  // Signup-code flow state
  const [signupFlowActive, setSignupFlowActive] = useState<boolean>(false);
  const [signupToken, setSignupToken] = useState<string | null>(null);
  // Name no longer required in signup flow
  const [autoCreateTriggered, setAutoCreateTriggered] = useState<boolean>(false);
  // Prevent duplicate verify-user API calls in React StrictMode
  const hasVerifiedSignupRef = useRef<boolean>(false);

  // Helpers to manage magic-link UI state
  const resetMagicLinkState = (): void => {
    setIsRateLimited(false);
    setRateLimitTimer(0);
    setResendAttempts(0);
    setResendTimer(0);
    setShowResendOption(false);
    setMagicLinkSent(false);
    setEmailError('');
    setEmailTouched(false);
    setRateLimitMessage('');
  };

  // Ensure Google button loading is cleared when returning from OAuth (back/nav) or on mount
  useEffect(() => {
    // Clear any stale googleAuth loading flag
    setLoading('googleAuth', false);

    const resetGoogleLoading = () => setLoading('googleAuth', false);

    window.addEventListener('focus', resetGoogleLoading);
    window.addEventListener('pageshow', resetGoogleLoading);
    document.addEventListener('visibilitychange', resetGoogleLoading);

    return () => {
      window.removeEventListener('focus', resetGoogleLoading);
      window.removeEventListener('pageshow', resetGoogleLoading);
      document.removeEventListener('visibilitychange', resetGoogleLoading);
    };
  }, [setLoading]);

  const onMagicLinkSent = (): void => {
    setMagicLinkSent(true);
    setShowResendOption(false);
    setEmailError('');
    setEmailTouched(false);
  };

  const isMagicLinkSuccessStatus = (status?: string): boolean => {
    return status === '$ok' || status === 'MAGIC_LINK_SENT';
  };

  // Show an immediate verifying state when URL contains signupcode to avoid form flicker
  const [verifyingSignup, setVerifyingSignup] = useState<boolean>(() => {
    try {
      const search = typeof window !== 'undefined' ? window.location.search : '';
      const hash = typeof window !== 'undefined' ? window.location.hash : '';
      const urlParams = new URLSearchParams(search);
      const hashParams = new URLSearchParams(hash.split('?')[1] || '');
      const code = urlParams.get('signupcode') || hashParams.get('signupcode');
      return !!code;
    } catch {
      return false;
    }
  });

  // Type guards for flexible backend responses (avoid assertions/any)
  const hasTokenFields = (v: unknown): v is { token?: string; refreshToken?: string } => {
    return typeof v === 'object' && v !== null && ('token' in v || 'refreshToken' in v);
  };
  const hasAccessTokenFields = (v: unknown): v is { access_token?: string; refresh_token?: string } => {
    return typeof v === 'object' && v !== null && ('access_token' in v || 'refresh_token' in v);
  };
  const hasNestedUser = (v: unknown): v is { user: import('../../shared/api/types').User } => {
    return typeof v === 'object' && v !== null && 'user' in v;
  };
  const hasFlatUserFields = (v: unknown): v is {
    emailAddress: string;
    displayName: string;
    userId: number;
    userName: string;
    forceChangePassword: boolean;
    twoStepVerificationRequired: boolean;
    onboarding?: { completed: boolean; currentStep: number };
  } => {
    return (
      typeof v === 'object' && v !== null &&
      'emailAddress' in v && 'userId' in v && 'userName' in v
    );
  };



  // Auto-advance carousel every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % carouselImages.length);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  // Barclays pre-arrears: persist valid `ct` token when present (invalid tokens ignored)
  useEffect(() => {
    persistCtIfValid();
  }, []);

  // Detect signupcode in URL and verify token
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const hashParams = new URLSearchParams(location.hash.split('?')[1] || '');
    const code = urlParams.get('signupcode') || hashParams.get('signupcode');
    if (!code) {
      setVerifyingSignup(false);
      return;
    }

    // Prevent duplicate calls in React StrictMode - verification tokens can only be used once
    if (hasVerifiedSignupRef.current) {
      return;
    }
    hasVerifiedSignupRef.current = true;

    setVerifyingSignup(true);
    setLoading('verifySignup', true);

    (async () => {
      try {
        const resp = await AuthService.verifyUser({ token: code });
        // Support both raw and envelope shapes
        const payload: unknown = resp as unknown;
        const isEnvelope = typeof payload === 'object' && payload !== null && 'success' in (payload as any);
        const verified = isEnvelope
          ? ((payload as any).data?.verified as string | undefined)
          : ((payload as any).verified as string | undefined);
        const email = isEnvelope
          ? ((payload as any).data?.email as string | undefined)
          : ((payload as any).email as string | undefined);

        if (verified === 'Y' && email) {
          setSignupFlowActive(true);
          setSignupToken(code);
          setFormData((prev: AuthScreenData) => ({ ...prev, email }));
          setDialogType('signup');
          // Open mobile bottom sheet for signup flow
          setShowLoginOptions(false);
          setLoginBottomSheetOpen(true);
          setAutoCreateTriggered(false);
          showSuccess('Email verified. Creating your account...');
        } else {
          showError('Invalid or expired signup link.');
          setLinkExpired(true);
          setShowLoginOptions(false);
        }
      } catch (e) {
        // Check for specific token expiration errors
        let apiMessage = '';
        let apiCode = '';
        let apiDetails: unknown = null;

        if (typeof e === 'object' && e !== null && 'response' in (e as Record<string, unknown>)) {
          const respUnknown = (e as Record<string, unknown>)['response'];
          if (typeof respUnknown === 'object' && respUnknown !== null && 'data' in (respUnknown as Record<string, unknown>)) {
            const dataUnknown = (respUnknown as Record<string, unknown>)['data'];
            if (typeof dataUnknown === 'object' && dataUnknown !== null) {
              const d = dataUnknown as Record<string, unknown>;
              const msgVal = d['message'];
              const codeVal = d['errorCode'];
              const detailsVal = d['details'];
              apiMessage = typeof msgVal === 'string' ? msgVal : '';
              apiCode = typeof codeVal === 'string' ? codeVal : '';
              apiDetails = detailsVal;
            }
          }
        }

        const isExpired =
          (apiMessage.toLowerCase().includes('expired')) ||
          (apiMessage.toLowerCase().includes('invalid token')) ||
          apiCode === 'AUTH_ERROR';

        if (isExpired) {
          setLinkExpired(true);
          setShowLoginOptions(false);
          setLoginBottomSheetOpen(false);
          setErrorSheetOpen(true);
        } else {
          let detailMessage = '';
          if (apiDetails && typeof apiDetails === 'object' && apiDetails !== null) {
            const dd = apiDetails as Record<string, unknown>;
            const inner = dd['detail'];
            if (typeof inner === 'string') detailMessage = inner;
          }
          setVerifyError({
            message: apiMessage || 'Failed to verify signup link.',
            ...(detailMessage ? { description: detailMessage } : {})
          });
          setShowLoginOptions(false);
          setLoginBottomSheetOpen(false);
          setErrorSheetOpen(true);
        }
      } finally {
        setLoading('verifySignup', false);
        setVerifyingSignup(false);
      }
    })();
  }, [location.search, location.hash, setLoading, showSuccess, showError]);

  // Timer for showing resend option after magic link is sent (initial send only)
  useEffect(() => {
    if (
      magicLinkSent &&
      !showResendOption &&
      !isRateLimited &&
      resendAttempts === 0 &&
      resendTimer === 0
    ) {
      const timerId = setTimeout(() => {
        setShowResendOption(true);
      }, 5000);

      return () => clearTimeout(timerId);
    }
    return undefined;
  }, [magicLinkSent, showResendOption, isRateLimited, resendAttempts, resendTimer]);

  // Timer for resend cooldown (post-resend only)
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => {
        setResendTimer(prev => prev - 1);
      }, 1000);

      return () => clearTimeout(timer);
    } else if (resendTimer === 0 && !isRateLimited && magicLinkSent && resendAttempts > 0) {
      // Show resend option after cooldown completes (only for resends)
      setShowResendOption(true);
    }
    return undefined;
  }, [resendTimer, isRateLimited, magicLinkSent, resendAttempts]);

  // Timer for rate limit cooldown (15 minutes)
  useEffect(() => {
    if (rateLimitTimer > 0) {
      const timer = setTimeout(() => {
        setRateLimitTimer(prev => prev - 1);
      }, 1000);

      return () => clearTimeout(timer);
    } else if (rateLimitTimer === 0 && isRateLimited) {
      setIsRateLimited(false);
      setResendAttempts(0);
    }
    return undefined;
  }, [rateLimitTimer, isRateLimited]);

  const handleInputChange = (field: keyof AuthScreenData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    // If user edits email to a different address, clear rate-limit/resend state and magic screen
    if (field === 'email') {
      const prevEmail = formData.email;
      const normalizedPrev = prevEmail.trim().toLowerCase();
      const normalizedNext = value.trim().toLowerCase();
      if (normalizedPrev !== normalizedNext) {
        if (isRateLimited || resendAttempts > 0 || resendTimer > 0 || showResendOption || magicLinkSent) {
          resetMagicLinkState();
        }
      }
      // Clear error when user starts typing again
      if (emailError) {
        setEmailError('');
        setEmailTouched(false);
      }
    }
    setFormData((prev: AuthScreenData) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEmailBlur = () => {
    // Trim leading and trailing whitespace when field loses focus
    const trimmedEmail = formData.email.trim();
    if (trimmedEmail !== formData.email) {
      setFormData((prev: AuthScreenData) => ({
        ...prev,
        email: trimmedEmail
      }));
    }
    // Don't show error on blur, only on Continue button click
    // Just mark as touched for internal tracking if needed
  };

  const handleSendMagicLink = async () => {
    // Trim email before validation and submission
    const trimmedEmail = formData.email.trim();
    if (trimmedEmail !== formData.email) {
      setFormData((prev: AuthScreenData) => ({
        ...prev,
        email: trimmedEmail
      }));
    }

    // Always validate email before sending
    const error = getEmailError(trimmedEmail);
    if (error) {
      setEmailTouched(true);
      setEmailError(error);
      // Show error but don't proceed
      return;
    }

    // Set loading state
    setLoading('magicLink', true);

    try {
      const response = await AuthService.requestMagicLink({ email: trimmedEmail });

      // Check for successful response - if we reach here, the HTTP status was 200
      if (isMagicLinkSuccessStatus(response.status)) {
        onMagicLinkSent();
      } else {
        // Handle API-level errors and rate limiting
        // Extract error message from various possible response structures
        const responseObj = response as unknown as Record<string, unknown>;
        let apiMsg = '';
        let detailMsg = '';

        // Try to get message from response
        if (typeof responseObj.message === 'string') {
          apiMsg = responseObj.message;
        }

        // Also check details.detail for more specific info
        if (
          responseObj.details &&
          typeof responseObj.details === 'object' &&
          'detail' in responseObj.details &&
          typeof (responseObj.details as Record<string, unknown>).detail === 'string'
        ) {
          detailMsg = (responseObj.details as Record<string, unknown>).detail as string;
        }

        const combinedMsg = `${apiMsg} ${detailMsg}`.toLowerCase();
        const exceeded = /maximum\s+sign[- ]?in\s+attempts\s+exceeded/i.test(combinedMsg);

        if (responseObj.status === '$error' && exceeded) {
          setIsRateLimited(true);
          setRateLimitMessage(apiMsg || detailMsg);
          setRateLimitTimer(extractCooldownSeconds(apiMsg || detailMsg));
          setShowResendOption(false);
          // Show the rate-limited screen
          setMagicLinkSent(true);
        } else {
          // Map technical/generic errors to user-friendly messages
          let userFriendlyMessage = '';

          if (detailMsg.toLowerCase().includes('send signin link failed') ||
            detailMsg.toLowerCase().includes('send sign-in link failed')) {
            userFriendlyMessage = 'Unable to send the sign-in link. Please check your email address and try again.';
          } else if (apiMsg.toLowerCase().includes('unexpected error') ||
            apiMsg.toLowerCase().includes('internal error')) {
            userFriendlyMessage = 'Something went wrong while sending your sign-in link. Please try again.';
          } else if (detailMsg) {
            userFriendlyMessage = detailMsg;
          } else if (apiMsg) {
            userFriendlyMessage = apiMsg;
          } else {
            userFriendlyMessage = 'Failed to send magic link. Please try again.';
          }

          // Auto-dismiss user-friendly error messages after 6 seconds
          showError(userFriendlyMessage, { autoHide: true, duration: 6000 });
        }
      }
    } catch (error) {
      // Handle HTTP errors (non-200 status codes) and detect rate limiting
      const maybeResponse = (error as { response?: { data?: unknown } })?.response?.data;
      let apiMessage = '';
      let detailMessage = '';

      if (maybeResponse && typeof maybeResponse === 'object') {
        const responseObj = maybeResponse as Record<string, unknown>;

        // Extract main message
        if (typeof responseObj.message === 'string' && responseObj.message.trim()) {
          apiMessage = responseObj.message;
        }

        // Extract details.detail for more specific error info
        if (
          responseObj.details &&
          typeof responseObj.details === 'object' &&
          'detail' in responseObj.details &&
          typeof (responseObj.details as Record<string, unknown>).detail === 'string'
        ) {
          detailMessage = (responseObj.details as Record<string, unknown>).detail as string;
        }
      }

      // Fallback to error message if no API message
      if (!apiMessage) {
        const fallback = (error as { message?: string })?.message;
        apiMessage = typeof fallback === 'string' && fallback.trim() ? fallback : '';
      }

      // Check for rate limiting
      const combinedMessage = `${apiMessage} ${detailMessage}`.toLowerCase();
      const exceeded = /maximum\s+sign[- ]?in\s+attempts\s+exceeded/i.test(combinedMessage);

      if (exceeded) {
        setIsRateLimited(true);
        setRateLimitMessage(apiMessage || detailMessage);
        setRateLimitTimer(extractCooldownSeconds(apiMessage || detailMessage));
        setShowResendOption(false);
        // Show the rate-limited screen
        setMagicLinkSent(true);
      } else {
        // Map technical/generic errors to user-friendly messages
        let userFriendlyMessage = '';

        if (detailMessage.toLowerCase().includes('send signin link failed') ||
          detailMessage.toLowerCase().includes('send sign-in link failed')) {
          userFriendlyMessage = 'Unable to send the sign-in link. Please check your email address and try again.';
        } else if (apiMessage.toLowerCase().includes('unexpected error') ||
          apiMessage.toLowerCase().includes('internal error')) {
          userFriendlyMessage = 'Something went wrong while sending your sign-in link. Please try again.';
        } else if (detailMessage) {
          userFriendlyMessage = detailMessage;
        } else if (apiMessage) {
          userFriendlyMessage = apiMessage;
        } else {
          userFriendlyMessage = 'Failed to send magic link. Please try again.';
        }

        // Auto-dismiss user-friendly error messages after 6 seconds
        showError(userFriendlyMessage, { autoHide: true, duration: 6000 });
      }
    } finally {
      // Clear loading state
      setLoading('magicLink', false);
    }
  };

  const handleResendMagicLink = async () => {

    try {
      const response = await AuthService.requestMagicLink({ email: formData.email });
      // Check for successful response - if we reach here, the HTTP status was 200
      if (isMagicLinkSuccessStatus(response.status)) {
        onMagicLinkSent();
      } else {
        // Handle API-level errors
        const errorMessage = response.message || 'Failed to send magic link. Please try again.';
        showError(errorMessage, { autoHide: true, duration: 6000 });
      }
    } catch (_error) {
      // Handle HTTP errors (non-200 status codes)
      showError('Failed to send magic link. Please try again.', { autoHide: true, duration: 6000 });
    }

    const newAttempts = resendAttempts + 1;
    setResendAttempts(newAttempts);

    if (newAttempts >= 2) {
      // Rate limit after 2 attempts - 15 minutes (900 seconds)
      setIsRateLimited(true);
      setRateLimitTimer(900);
      setShowResendOption(false);
      // Hint iOS Safari to repaint by remounting Drawer content on next frame
      requestAnimationFrame(() => setContentEpoch((prev) => prev + 1));
    } else {
      // Set timer for next resend (10 seconds)
      setResendTimer(10);
      setShowResendOption(false);
      // Also remount on normal resend to avoid stale paint on some devices
      requestAnimationFrame(() => setContentEpoch((prev) => prev + 1));
    }

    // Ensure the magic-link screen remains visible after resends (iOS Safari can glitch on re-render)
    if (!magicLinkSent) {
      setMagicLinkSent(true);
    }
  };


  const handleGoogleLogin = async () => {
    setLoading('googleAuth', true);
    try {
      const response = await AuthService.getGoogleAuthUrl();

      if (response.success && response.data?.state && response.data?.url) {
        // Store state for CSRF protection
        sessionStorage.setItem('oauth_google_state', response.data.state);
        // preserve "from" route (if user came from a protected deep link)
        const from = (location.state as any)?.from;
        if (from) sessionStorage.setItem('post_auth_from', from);

        // Redirect to Google
        window.location.href = response.data.url;
      } else {
        throw new Error(response.message || 'Failed to get Google auth URL');
      }
    } catch (error) {
      const errorMessage = (error as any)?.response?.data?.message || 'Failed to initialize Google login. Please try again.';
      showError(errorMessage);
      setLoading('googleAuth', false);
    }
  };

  const handleCreateAccount = async () => {
    if (!signupFlowActive || !signupToken) return;

    // Trim email before validation and submission
    const trimmedEmail = formData.email.trim();
    if (trimmedEmail !== formData.email) {
      setFormData((prev: AuthScreenData) => ({
        ...prev,
        email: trimmedEmail
      }));
    }

    if (!trimmedEmail) {
      showError('Email not available for signup.', { autoHide: true, duration: 6000 });
      return;
    }

    setLoading('createUser', true);
    try {
      const qs = getStoredCtTokenIfValid();
      // Create account automatically without name
      const resp = await AuthService.createUser({ email: trimmedEmail, ...(qs ? { qs } : {}) });
      // Handle both envelope { success, data } and raw payload shapes
      const maybeEnvelope: any = resp as any;
      const payload: any = (maybeEnvelope && typeof maybeEnvelope === 'object' && 'success' in maybeEnvelope && maybeEnvelope.success && maybeEnvelope.data)
        ? maybeEnvelope.data
        : maybeEnvelope;

      const token = hasTokenFields(payload) && payload.token
        ? payload.token!
        : (hasAccessTokenFields(payload) && payload.access_token) ? payload.access_token! : '';
      const refreshToken = hasTokenFields(payload) && payload.refreshToken
        ? payload.refreshToken
        : (hasAccessTokenFields(payload) ? payload.refresh_token : undefined);

      let userObj: import('../../shared/api/types').User | null = null;
      if (hasNestedUser(payload)) {
        userObj = payload.user;
      } else if (hasFlatUserFields(payload)) {
        userObj = {
          emailAddress: payload.emailAddress,
          displayName: payload.displayName,
          userId: payload.userId,
          userName: payload.userName,
          forceChangePassword: payload.forceChangePassword,
          twoStepVerificationRequired: payload.twoStepVerificationRequired,
          onboarding: payload.onboarding || { completed: false, currentStep: 0 }
        };
      }

      if (token && userObj) {
        // Legacy parity: fire Meta Pixel + CAPI event after creating account
        void trackMetaEvent('Lead', 'Lead');
        
        completeAuth({ user: userObj, token, ...(refreshToken ? { refreshToken } : {}) });
        showSuccess('Account created successfully.');
        // Clear browser query params before navigating to avoid carrying over old params
        if (window.location.search) {
          window.history.replaceState(null, '', window.location.pathname + window.location.hash);
        }
        const target = getOnboardingRedirect(userObj) || '/new-onboarding?step=0';
        navigate(target, { replace: true });
      } else {
        const errMsg = maybeEnvelope?.message || 'Signup succeeded but session could not be established.';
        showError(errMsg);
        // Fall back to normal auth UI so we don't get stuck on the signup loading state
        setSignupFlowActive(false);
        setDialogType('login');
        setShowLoginOptions(false);
        setLoginBottomSheetOpen(true);
      }
    } catch (error) {
      const apiData: any = (error as any)?.response?.data;
      const message: string = apiData?.errorMessage || apiData?.message || apiData?.error?.message || 'Failed to create account. Please try again.';
      const alreadyExists: boolean = (
        (typeof apiData?.errorTitle === 'string' && apiData.errorTitle.includes('UserAlreadyExistsException')) ||
        (typeof apiData?.errorMessage === 'string' && /already registered/i.test(apiData.errorMessage)) ||
        (typeof apiData?.message === 'string' && /already registered/i.test(apiData.message))
      );

      // Treat INTERNAL_ERROR during create-user as expired link scenario
      const errorCode: string = typeof apiData?.errorCode === 'string' ? apiData.errorCode : '';
      const detailsObj: unknown = apiData?.details;
      let detailsText = '';
      if (detailsObj && typeof detailsObj === 'object' && detailsObj !== null) {
        const d = detailsObj as Record<string, unknown>;
        if (typeof d.detail === 'string') detailsText = d.detail;
      }
      const isCreateUserInternalFailure = errorCode === 'INTERNAL_ERROR' && /user creation failed/i.test(detailsText);
      if (isCreateUserInternalFailure) {
        // Show the existing link-expired dialog
        setLinkExpired(true);
        setShowLoginOptions(false);
        setLoginBottomSheetOpen(false);
        setErrorSheetOpen(true);
        return;
      }

      if (alreadyExists) {
        showWarning(message);
      } else {
        showError(message);
      }

      if (alreadyExists) {
        // Switch to login with prefilled email so user can sign in via magic link
        setSignupFlowActive(false);
        setDialogType('login');
        setShowLoginOptions(false);
        setLoginBottomSheetOpen(true);
      } else {
        // Fall back to normal auth UI so we don't get stuck on the signup loading state
        setSignupFlowActive(false);
        setDialogType('login');
        setShowLoginOptions(false);
        setLoginBottomSheetOpen(true);
      }
    } finally {
      setLoading('createUser', false);
    }
  };

  // Auto-create account once verification has succeeded and email is set
  useEffect(() => {
    if (signupFlowActive && signupToken && formData.email && !autoCreateTriggered) {
      setAutoCreateTriggered(true);
      void handleCreateAccount();
    }
  }, [signupFlowActive, signupToken, formData.email, autoCreateTriggered]);

  // Detect if user is on mobile device
  const isMobileDevice = (): boolean => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  // Open Inbox handler: open default mail app via mailto
  const openDefaultMailApp = (): void => {
    window.location.href = 'googlegmail://';
  };

  // Attempt to open app via deep link with a safe web fallback
  const openUrlWithFallback = (primaryUrl: string, fallbackUrl: string): void => {
    const start = Date.now();
    const fallbackTimer = window.setTimeout(() => {
      if (Date.now() - start < 2000) {
        window.open(fallbackUrl, '_blank', 'noopener,noreferrer');
      }
    }, 1200);

    const clear = () => window.clearTimeout(fallbackTimer);
    window.addEventListener('pagehide', clear, { once: true });
    window.addEventListener('blur', clear, { once: true });
    try {
      window.location.href = primaryUrl;
    } catch {
      clear();
      window.open(fallbackUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const openProvider = (provider: 'gmail' | 'outlook' | 'yahoo' | 'icloud') => {
    const webUrls: Record<typeof provider, string> = {
      gmail: 'https://mail.google.com/mail/u/0/#inbox',
      outlook: 'https://outlook.live.com/mail/0/inbox',
      yahoo: 'https://mail.yahoo.com',
      icloud: 'https://www.icloud.com/mail'
    } as const;

    if (isMobileDevice()) {
      // Best-effort deep links for popular apps; will no-op if not installed
      const deepLinks: Record<typeof provider, string | null> = {
        gmail: 'googlegmail://', // or 'gmail://'
        outlook: 'ms-outlook://',
        yahoo: 'ymail://',
        icloud: null // iOS Mail does not expose a public inbox deeplink
      } as const;

      const primary = deepLinks[provider];
      const fallback = webUrls[provider];
      if (primary) {
        openUrlWithFallback(primary, fallback);
      } else {
        window.open(fallback, '_blank', 'noopener,noreferrer');
      }
      setMailChooserOpen(false);
      return;
    }

    // Desktop: open web inbox
    window.open(webUrls[provider], '_blank', 'noopener,noreferrer');
    setMailChooserOpen(false);
  };

  const resetToLogin = () => {
    setDialogType('login');
    setSignupFlowActive(false);
    setSignupToken(null);
    setAutoCreateTriggered(false);
    resetMagicLinkState();
    setFormData({ email: '' });
    setLinkExpired(false);
  };

  const handleCloseBottomSheet = () => {
    setLoginBottomSheetOpen(false);
    setShowLoginOptions(true);
    resetToLogin();
  };


  // handleLoginWithEmail removed — login is now via magic link from UW support




  const { openFaq } = useHelp();
  const handleHelpOpen = () => openFaq(dialogType === 'login' ? 'auth.login' : 'auth.signup');

  // Detect navigation state from verify-email for error payloads and expired link
  useEffect(() => {
    const navState = (location as unknown as { state?: unknown })?.state as
      | { linkExpired?: boolean; magicLinkError?: unknown }
      | undefined;

    if (!navState) return;

    const { linkExpired: navExpired, magicLinkError } = navState;

    const hasError = isMagicLinkError(magicLinkError);

    // Expired link: show inline "Link Expired" screen on /auth (no dialog)
    if (navExpired) {
      setVerifyError(null);
      setErrorSheetOpen(false);
      setLinkExpired(true);

      // Ensure we land on the email login form (especially on mobile)
      setDialogType('login');
      setSignupFlowActive(false);
      setSignupToken(null);
      setAutoCreateTriggered(false);
      resetMagicLinkState();
      setFormData({ email: '' });
      setShowLoginOptions(false);
      setLoginBottomSheetOpen(true);

      // Clear the state so refresh/back doesn't re-trigger
      try {
        navigate('/auth', { replace: true, state: {} as any });
      } catch { }
      return;
    }

    // Other magic-link verification errors: keep existing error dialog/bottom-sheet
    if (hasError) {
      const nextError = typeof magicLinkError.description === 'string'
        ? { message: magicLinkError.message, description: magicLinkError.description }
        : { message: magicLinkError.message };
      setVerifyError(nextError);
      setShowLoginOptions(false);
      setLoginBottomSheetOpen(false);
      setErrorSheetOpen(true);
      // Clear the state so refresh/back doesn't re-trigger
      try {
        navigate('/auth', { replace: true, state: {} as any });
      } catch { }
    }
  }, [location, navigate]);

  // Full-screen verifying view to prevent showing the email form while validating signup link
  const shouldShowUnifiedLoading =
    verifyingSignup ||
    loading.magicLink ||
    loading.googleAuth ||
    (signupFlowActive && !linkExpired && !errorSheetOpen);

  if (shouldShowUnifiedLoading) {
    const title = verifyingSignup
      ? 'Verifying your signup link...'
      : loading.googleAuth
        ? 'Redirecting to Google...'
        : loading.magicLink
          ? 'Sending your sign-in link...'
          : signupFlowActive
            ? (loading.createUser ? 'Creating your account...' : 'Preparing your account...')
            : 'Loading...';

    return <SimpleLoadingScreen title={title} />;
  }

  // Replace full-page error screens with modal dialogs rendered within main UI

  return (
    <>
      {/* Mobile - Show full-screen carousel with header */}
      <Box sx={{
        display: { xs: 'flex', md: 'none' },
        // Use dvh (dynamic viewport height) with 100vh fallback for older browsers
        height: '100dvh',
        minHeight: '100vh',
        width: '100vw',
        flexDirection: 'column',
        position: 'relative',
        // Account for safe areas on devices with notches
        paddingTop: 'env(safe-area-inset-top, 0px)'
      }}>
        {/* Mobile Header */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '4px 12px',
          width: '100%',
          height: '60px',
          background: '#FFFFFF',
          flex: 'none',
          order: 0,
          alignSelf: 'stretch',
          flexGrow: 0,
          position: 'relative',
          zIndex: 1300
        }}>
          {/* Logo Group */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              component="img"
              src="/icons/UPlusLogo/android-chrome-512x512.png"
              alt="UP"
              sx={{
                width: 44,
                height: 44,
                objectFit: 'contain',
                flexShrink: 0,
              }}
            />
          </Box>

          {/* Help Button */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, position: 'relative', zIndex: 1400 }}>
            {/* Help Button */}
            <Box
              onClick={handleHelpOpen}
              sx={{
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '4px 4px 4px 8px',
                gap: '10px',
                width: '71px',
                height: '28px',
                background: '#FFFFFF',
                border: '1px solid #D9D9D9',
                borderRadius: '17px',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#F5F5F5'
                }
              }}
            >
              <Typography sx={{
                fontFamily: 'Roboto',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
                color: '#033F44',
              }}>
                Help
              </Typography>
              <Box sx={{
                width: '20px',
                height: '20px',
                position: 'relative'
              }}>
                <Box sx={{
                  position: 'absolute',
                  left: '0%',
                  right: '0%',
                  top: '0%',
                  bottom: '0%',
                  background: '#000000',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  color: 'white',
                  fontWeight: 'bold'
                }}>
                  ?
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Carousel Content - Fills space between header and buttons */}
        <Box onTouchStart={onTouchStartCarousel} onTouchMove={onTouchMoveCarousel} onTouchEnd={onTouchEndCarousel} sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          width: '100%',
          overflow: 'hidden',
          // Reserve space to allow footer overlap, accounting for safe area
          paddingBottom: showLoginOptions ? 'calc(140px + env(safe-area-inset-bottom, 0px))' : '0px'
        }}>
          {/* Full Screen Carousel Image */}
          <Box
            component="img"
            src={carouselImages[currentImageIndex]}
            alt="Carousel"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transition: 'opacity 0.5s ease-in-out'
            }}
          />

          {/* Pagination dots */}
          <Box sx={{
            position: 'absolute',
            bottom: showLoginOptions ? 'calc(160px + env(safe-area-inset-bottom, 0px))' : 30,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            justifyContent: 'center',
            gap: 1.5,
            zIndex: 2
          }}>
            {carouselImages.map((_, index) => (
              <Box
                key={index}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: index === currentImageIndex ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.4)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
                onClick={() => setCurrentImageIndex(index)}
              />
            ))}
          </Box>
        </Box>

        {/* Fixed Bottom - Contact UW Support Message */}
        {showLoginOptions && (
          <Box sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            width: '100%',
            backgroundColor: '#FFFFFF',
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '20px 16px calc(24px + env(safe-area-inset-bottom, 0px))',
            paddingBottom: 'max(24px, env(safe-area-inset-bottom, 24px))',
            gap: '12px'
          }}>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px',
              width: '100%',
              maxWidth: '366px',
              textAlign: 'center'
            }}>
              {/* Lock icon */}
              <Box sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                bgcolor: '#E8F5E9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 0.5
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="11" width="18" height="11" rx="2" stroke="#033F44" strokeWidth="2" fill="none" />
                  <path d="M7 11V7a5 5 0 0110 0v4" stroke="#033F44" strokeWidth="2" strokeLinecap="round" fill="none" />
                </svg>
              </Box>
              <Typography sx={{
                fontFamily: 'Roboto',
                fontWeight: 600,
                fontSize: '18px',
                lineHeight: '24px',
                color: '#033F44',
              }}>
                Secure Login
              </Typography>
              <Typography sx={{
                fontFamily: 'Roboto',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '20px',
                color: '#666',
              }}>
                Reopen the magic link sent to your email to sign in again. Need a new link? Contact the UP support team.
              </Typography>
            </Box>
          </Box>
        )}

      </Box>

      {/* Desktop - Show both left form and right slideshow */}
      <Box sx={{
        height: '100vh',
        width: '100vw',
        display: { xs: 'none', md: 'flex' },
        // Keep the marketing carousel consistently on the left on desktop
        flexDirection: 'row',
        overflow: 'hidden',
        position: 'relative'
      }}>

        {/* Left Side - Form */}
        <Box sx={{
          flex: '1 1 auto',
          backgroundColor: { xs: 'transparent', md: 'grey.50' },
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          height: '100vh',
          overflow: 'auto',
          position: 'relative',
          zIndex: 1
        }}>
          {/* Desktop Container */}
          <Container maxWidth="sm" sx={{
            flex: 1,
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            px: 2,
            py: 2
          }}>
            {/* Desktop Dialog Content */}
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 3,
              width: '100%'
            }}>
              {/* Logo - Centered above dialog */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  component="img"
                  src="/icons/UPlusLogo/android-chrome-512x512.png"
                  alt="UP"
                  sx={{
                    width: 64,
                    height: 64,
                    objectFit: 'contain',
                    flexShrink: 0,
                  }}
                />
              </Box>
              {/* Desktop Dialog Card */}
              <Paper elevation={3} sx={{
                p: 3,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                width: '100%',
                maxWidth: '380px',
                position: 'relative',
                background: '#FFFFFF'
              }}>
                {(verifyError) ? (
                  // Skip inline desktop error card; dialog below will handle desktop
                  <></>
                ) : magicLinkSent ? (
                  /* Desktop Magic Link Sent Screen */
                  (() => {
                    const content = getEmailSentContent(resendAttempts, isRateLimited);
                    return (
                      <Box sx={{ textAlign: 'center' }}>
                        {/* Close Button */}
                        <Box sx={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          mb: 1,
                          position: 'absolute',
                          top: 12,
                          right: 12
                        }}>
                          <IconButton
                            onClick={resetToLogin}
                            sx={{
                              p: 1,
                              color: '#000000'
                            }}
                          >
                            <CloseIcon sx={{ fontSize: '18px' }} />
                          </IconButton>
                        </Box>

                        {/* Dynamic Image */}
                        <Box sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          mb: 2,
                          mt: 1.5
                        }}>
                          <Box
                            component="img"
                            src={content.image}
                            alt={content.title}
                            sx={{
                              width: '160px',
                              height: 'auto',
                              maxWidth: '100%'
                            }}
                          />
                        </Box>

                        {/* Dynamic Title */}
                        <Typography sx={{
                          fontFamily: 'Roboto',
                          fontWeight: 700,
                          fontSize: '22px',
                          lineHeight: '26px',
                          color: '#000000',
                          mb: 2,
                          textAlign: 'center'
                        }}>
                          {content.title}
                        </Typography>

                        {/* Dynamic Description */}
                        {isRateLimited ? (
                          <Box sx={{ mb: 2 }}>
                            <Typography sx={{
                              fontFamily: 'Roboto',
                              fontWeight: 400,
                              fontSize: '13px',
                              lineHeight: '18px',
                              color: '#9CA3AF',
                              textAlign: 'center',
                              mb: 1
                            }}>
                              {content.description}
                            </Typography>
                            {rateLimitMessage && (
                              <Typography sx={{
                                fontFamily: 'Roboto',
                                fontWeight: 500,
                                fontSize: '13px',
                                lineHeight: '18px',
                                color: '#d32f2f',
                                textAlign: 'center',
                                mb: 1
                              }}>
                                {rateLimitMessage}
                              </Typography>
                            )}
                            {rateLimitTimer > 0 && (
                              <Typography sx={{
                                fontFamily: 'Roboto',
                                fontWeight: 600,
                                fontSize: '16px',
                                lineHeight: '18px',
                                color: '#000000',
                                textAlign: 'center'
                              }}>
                                {formatTime(rateLimitTimer)}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Box sx={{ mb: 2 }}>
                            <Typography sx={{
                              fontFamily: 'Roboto',
                              fontWeight: 400,
                              fontSize: '13px',
                              lineHeight: '18px',
                              color: '#9CA3AF',
                              textAlign: 'center',
                              mb: 0.5
                            }}>
                              {content.description}
                            </Typography>
                            <Typography data-private sx={{
                              fontFamily: 'Roboto',
                              fontWeight: 600,
                              fontSize: '13px',
                              lineHeight: '18px',
                              color: 'primary.main',
                              textAlign: 'center',
                              textDecoration: 'underline'
                            }}>
                              {formData.email}
                            </Typography>
                            {content.moreDescription && (
                              <Typography sx={{
                                fontFamily: 'Roboto',
                                fontWeight: 400,
                                fontSize: '13px',
                                lineHeight: '18px',
                                color: '#9CA3AF',
                                textAlign: 'center',
                                mt: 0.5
                              }}>
                                {content.moreDescription}
                                {content.moreDescriptionBold ? (
                                  <Typography component="span" sx={{
                                    fontFamily: 'Roboto',
                                    fontWeight: 600,
                                    fontSize: '13px',
                                    lineHeight: '18px',
                                    color: '#000000',
                                    ml: 0.5
                                  }}>
                                    {content.moreDescriptionBold}
                                  </Typography>
                                ) : null}
                              </Typography>
                            )}
                          </Box>
                        )}

                        {/* Instruction */}
                        {!isRateLimited && (
                          <Typography sx={{
                            fontFamily: 'Roboto',
                            fontWeight: 400,
                            fontSize: '13px',
                            lineHeight: '18px',
                            color: '#9CA3AF',
                            textAlign: 'center',
                            mb: 2
                          }}>
                            {content.instruction}
                          </Typography>
                        )}

                        {/* Open Inbox Button - Only show if not rate limited */}
                        {/* {!isRateLimited && (
                          <UIButton
                            variant="contained"
                            size="large"
                            onClick={handleOpenInbox}
                            startIcon={
                              <Box sx={{
                                width: '16px',
                                height: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="#FFFFFF" strokeWidth="2" fill="none" />
                                  <polyline points="22,6 12,13 2,6" stroke="#FFFFFF" strokeWidth="2" fill="none" />
                                </svg>
                              </Box>
                            }
                            sx={{
                              width: '100%',
                              height: '40px',
                              backgroundColor: '#00717A',
                              borderRadius: '8px',
                              fontFamily: 'Roboto',
                              fontWeight: 500,
                              fontSize: '16px',
                              lineHeight: '20px',
                              color: '#FFFFFF',
                              textTransform: 'none',
                              boxShadow: 'none',
                              mb: 2,
                              '&:hover': {
                                backgroundColor: '#005a61',
                                boxShadow: 'none'
                              }
                            }}
                          >
                            Open inbox
                          </UIButton>
                        )} */}

                        {/* Resend actions - based on attempts */}
                        {!isRateLimited && (
                          resendAttempts === 0 ? (
                            showResendOption ? (
                              <Typography sx={{
                                fontFamily: 'Roboto',
                                fontWeight: 400,
                                fontSize: '12px',
                                lineHeight: '16px',
                                color: '#9CA3AF',
                                textAlign: 'center'
                              }}>
                                <>
                                  Didn't receive the email?{' '}
                                  <Link
                                    onClick={handleResendMagicLink}
                                    sx={{
                                      color: '#2563EB',
                                      textDecoration: 'underline',
                                      cursor: 'pointer',
                                      fontWeight: 500
                                    }}
                                  >
                                    Resend
                                  </Link>
                                  .
                                </>
                              </Typography>
                            ) : resendTimer > 0 ? (
                              <Typography sx={{
                                fontFamily: 'Roboto',
                                fontWeight: 400,
                                fontSize: '12px',
                                lineHeight: '16px',
                                color: '#9CA3AF',
                                textAlign: 'center'
                              }}>
                                Didn't receive the email? Try again in {formatTime(resendTimer)}
                              </Typography>
                            ) : (
                              <Typography sx={{
                                fontFamily: 'Roboto',
                                fontWeight: 400,
                                fontSize: '12px',
                                lineHeight: '16px',
                                color: '#9CA3AF',
                                textAlign: 'center'
                              }}>
                                Didn't receive the email? Please wait...
                              </Typography>
                            )
                          ) : (
                            <Typography sx={{
                              fontFamily: 'Roboto',
                              fontWeight: 400,
                              fontSize: '12px',
                              lineHeight: '16px',
                              color: '#9CA3AF',
                              textAlign: 'center'
                            }}>
                              {showResendOption ? (
                                <>
                                  Didn't receive the email?{' '}
                                  <Link
                                    onClick={handleResendMagicLink}
                                    sx={{
                                      color: '#2563EB',
                                      textDecoration: 'underline',
                                      cursor: 'pointer',
                                      fontWeight: 500
                                    }}
                                  >
                                    Try again
                                  </Link>
                                  .
                                </>
                              ) : resendTimer > 0 ? (
                                <>
                                  Didn't receive the email? Try again in {formatTime(resendTimer)}
                                </>
                              ) : (
                                <>
                                  Didn't receive the email? Please wait...
                                </>
                              )}
                            </Typography>
                          )
                        )}
                      </Box>
                    );
                  })()
                ) : (
                  /* Desktop - Contact UW Support Message */
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    {/* Header */}
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 3,
                      borderBottom: '1px solid #E5E7EB',
                      pb: 1.5
                    }}>
                      <Typography sx={{
                        fontFamily: 'Roboto',
                        fontWeight: 700,
                        fontSize: '20px',
                        lineHeight: '24px',
                        color: '#000000'
                      }}>
                        Welcome to Utilities Plus
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Link
                          onClick={handleHelpOpen}
                          sx={{
                            color: 'text.secondary',
                            textDecoration: 'underline',
                            fontSize: '0.875rem',
                            cursor: 'pointer',
                            '&:hover': { color: 'primary.main' }
                          }}
                        >
                          Help?
                        </Link>
                      </Box>
                    </Box>

                    {/* Lock icon */}
                    <Box sx={{
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      bgcolor: '#E8F5E9',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                    }}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="3" y="11" width="18" height="11" rx="2" stroke="#033F44" strokeWidth="2" fill="none" />
                        <path d="M7 11V7a5 5 0 0110 0v4" stroke="#033F44" strokeWidth="2" strokeLinecap="round" fill="none" />
                      </svg>
                    </Box>

                    <Typography sx={{
                      fontFamily: 'Roboto',
                      fontWeight: 600,
                      fontSize: '18px',
                      lineHeight: '24px',
                      color: '#033F44',
                      mb: 1.5,
                    }}>
                      Secure Login
                    </Typography>

                    <Typography sx={{
                      fontFamily: 'Roboto',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '22px',
                      color: '#666',
                      mb: 3,
                    }}>
                      Reopen the magic link sent to your email to sign in again. Need a new link? Contact the UP support team.
                    </Typography>

                    {/* GDPR Badge */}
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '6px',
                      mt: 1,
                    }}>
                      <Box
                        component="img"
                        src="/assets/onBoarding/gdpr.svg"
                        alt="GDPR"
                        sx={{ width: 32, height: 32 }}
                      />
                      <Typography sx={{
                        fontFamily: 'Roboto',
                        fontWeight: 500,
                        fontSize: '12px',
                        color: '#101727',
                        textAlign: 'center'
                      }}>
                        Secure & Confidential
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Paper>
              <PoweredByIEHub />
            </Box>
          </Container>
        </Box>

        {/* Right Side - Carousel - Desktop Only */}
        <Box sx={{
          flex: '0 0 auto',
          width: 'max(5vw, calc(100vh * 9 / 16))',
          display: { xs: 'none', md: 'flex' },
          alignItems: 'flex-start',
          justifyContent: 'flex-end',
          position: 'relative',
          height: '100vh',
          overflow: 'hidden'
        }}>
          {/* 9:16 Image Wrapper */}
          <Box
            onTouchStart={onTouchStartCarousel}
            onTouchMove={onTouchMoveCarousel}
            onTouchEnd={onTouchEndCarousel}
            sx={{
              width: '100%',
              aspectRatio: '9 / 16'
            }}
          >
            <Box
              component="img"
              src={carouselImages[currentImageIndex]}
              alt="Carousel"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                objectPosition: 'left center',
                transition: 'opacity 0.5s ease-in-out'
              }}
            />
          </Box>

          {/* Pagination dots */}
          <Box sx={{
            position: 'absolute',
            bottom: 30,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            justifyContent: 'center',
            gap: 1.5,
            zIndex: 2
          }}>
            {carouselImages.map((_, index) => (
              <Box
                key={index}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: index === currentImageIndex ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.4)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
                onClick={() => setCurrentImageIndex(index)}
              />
            ))}
          </Box>
        </Box>
      </Box>


      {/* Mobile Bottom Sheet for Login */}
      <Drawer
        anchor="bottom"
        open={loginBottomSheetOpen}
        onClose={handleCloseBottomSheet}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            background: '#FFFFFF',
            borderRadius: '12px 12px 0px 0px',
            maxHeight: '85vh',
            height: 'auto',
            overflow: 'hidden',
            padding: 0,
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            // iOS Safari: force GPU compositing to avoid white flashes
            transform: 'translateZ(0)',
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            willChange: 'transform'
          }
        }}
      >
        <Box key={contentEpoch} sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '0px',
          gap: '12px',
          width: '100%',
          minHeight: 'fit-content',
          maxHeight: '85vh',
          paddingBottom: '20px'
        }}>
          {magicLinkSent ? (
            (() => {
              const content = getEmailSentContent(resendAttempts, isRateLimited);
              return (
                <>
                  {/* Frame 46 - Close Button Container */}
                  <Box sx={{
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    padding: '16px',
                    gap: '2px',
                    width: '100%',
                    height: '53px',
                    borderBottom: '1px solid #E5E7EB',
                    flex: 'none',
                    order: 0,
                    flexGrow: 0
                  }}>
                    <IconButton
                      onClick={resetToLogin}
                      sx={{
                        width: '14px',
                        height: '14px',
                        p: 0,
                        color: '#000000',
                        flex: 'none',
                        order: 0,
                        flexGrow: 0
                      }}
                    >
                      <CloseIcon sx={{ fontSize: '18px' }} />
                    </IconButton>
                  </Box>

                  {/* Frame 14583 - Main Content Container */}
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '0px 16px 16px',
                    gap: '16px',
                    width: '100%',
                    flex: 1,
                    order: 1,
                    alignSelf: 'stretch',
                    flexGrow: 0
                  }}>
                    {/* Frame 14637 - Content Wrapper */}
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      padding: '0px',
                      gap: '24px',
                      width: '100%',
                      maxWidth: '342px',
                      flex: 'none',
                      order: 0,
                      flexGrow: 0
                    }}>
                      {/* Frame 14609 - Dynamic Image */}
                      <Box sx={{
                        width: '100%',
                        maxWidth: '280px',
                        height: 'auto',
                        minHeight: '180px',
                        maxHeight: '180px',
                        flex: 'none',
                        order: 0,
                        flexGrow: 0,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}>
                        <Box
                          component="img"
                          src={content.image}
                          alt={content.title}
                          sx={{
                            width: '100%',
                            height: 'auto',
                            maxWidth: '280px',
                            maxHeight: '180px',
                            objectFit: 'contain'
                          }}
                        />
                      </Box>

                      {/* Frame 14608 - Text and Button Container */}
                      <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        padding: '0px',
                        gap: '32px',
                        width: '100%',
                        maxWidth: '342px',
                        flex: 'none',
                        order: 1,
                        flexGrow: 0
                      }}>
                        {/* Frame 14607 - Text Container */}
                        <Box sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          padding: '0px',
                          gap: '24px',
                          width: '100%',
                          maxWidth: '342px',
                          flex: 'none',
                          order: 0,
                          alignSelf: 'stretch',
                          flexGrow: 0
                        }}>
                          {/* Dynamic Title */}
                          <Typography sx={{
                            fontFamily: 'Roboto',
                            fontStyle: 'normal',
                            fontWeight: 700,
                            fontSize: '24px',
                            lineHeight: '28px',
                            textAlign: 'center',
                            color: '#000000',
                            flex: 'none',
                            order: 0,
                            flexGrow: 0
                          }}>
                            {content.title}
                          </Typography>

                          {/* Frame 14606 - Description Container */}
                          <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            padding: '0px',
                            gap: '12px',
                            width: '100%',
                            maxWidth: '342px',
                            flex: 'none',
                            order: 1,
                            alignSelf: 'stretch',
                            flexGrow: 0
                          }}>
                            {/* Dynamic Description */}
                            {isRateLimited ? (
                              <>
                                <Typography sx={{
                                  width: '100%',
                                  fontFamily: 'Roboto',
                                  fontStyle: 'normal',
                                  fontWeight: 400,
                                  fontSize: '16px',
                                  lineHeight: '19px',
                                  textAlign: 'center',
                                  color: 'rgba(0, 0, 0, 0.54)',
                                  flex: 'none',
                                  order: 0,
                                  alignSelf: 'stretch',
                                  flexGrow: 0
                                }}>
                                  {content.description}
                                </Typography>
                                {rateLimitMessage && (
                                  <Typography sx={{
                                    width: '100%',
                                    fontFamily: 'Roboto',
                                    fontStyle: 'normal',
                                    fontWeight: 600,
                                    fontSize: '16px',
                                    lineHeight: '19px',
                                    textAlign: 'center',
                                    color: '#d32f2f',
                                    flex: 'none',
                                    order: 1,
                                    alignSelf: 'stretch',
                                    flexGrow: 0
                                  }}>
                                    {rateLimitMessage}
                                  </Typography>
                                )}
                                {rateLimitTimer > 0 && (
                                  <Typography sx={{
                                    width: '100%',
                                    fontFamily: 'Roboto',
                                    fontStyle: 'normal',
                                    fontWeight: 700,
                                    fontSize: '20px',
                                    lineHeight: '24px',
                                    textAlign: 'center',
                                    color: '#000000',
                                    flex: 'none',
                                    order: 2,
                                    alignSelf: 'stretch',
                                    flexGrow: 0,
                                    mt: 1
                                  }}>
                                    {formatTime(rateLimitTimer)}
                                  </Typography>
                                )}
                              </>
                            ) : (
                              <>
                                <Typography
                                  component="div"
                                  sx={{
                                    width: '100%',
                                    fontFamily: 'Roboto',
                                    fontStyle: 'normal',
                                    fontWeight: 400,
                                    fontSize: '16px',
                                    lineHeight: '19px',
                                    textAlign: 'center',
                                    color: 'rgba(0, 0, 0, 0.54)',
                                    flex: 'none',
                                    order: 0,
                                    alignSelf: 'stretch',
                                    flexGrow: 0
                                  }}
                                >
                                  {content.description}
                                  <Typography
                                    component="span"
                                    data-private
                                    sx={{
                                      fontWeight: 400,
                                      color: '#000000',
                                      textDecoration: 'underline',
                                      ml: 0.5
                                    }}
                                  >
                                    {formData.email}
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexDirection: 'row',
                                      justifyContent: 'center',
                                      alignItems: 'center',
                                      gap: '4px',
                                      mt: 0.5
                                    }}
                                  >
                                    <Typography
                                      component="span"
                                      sx={{
                                        fontFamily: 'Roboto',
                                        fontStyle: 'normal',
                                        fontWeight: 400,
                                        fontSize: '16px',
                                        lineHeight: '19px',
                                        color: 'rgba(0, 0, 0, 0.54)'
                                      }}
                                    >
                                      {content.moreDescription}
                                    </Typography>
                                    <Typography
                                      component="span"
                                      sx={{
                                        fontFamily: 'Roboto',
                                        fontStyle: 'normal',
                                        fontWeight: 600,
                                        fontSize: '16px',
                                        lineHeight: '19px',
                                        color: '#000000'
                                      }}
                                    >
                                      {content.moreDescriptionBold}
                                    </Typography>
                                  </Box>
                                </Typography>
                                <Typography
                                  sx={{
                                    width: '100%',
                                    fontFamily: 'Roboto',
                                    fontStyle: 'normal',
                                    fontWeight: 600,
                                    fontSize: '16px',
                                    lineHeight: '19px',
                                    textAlign: 'center',
                                    color: 'rgba(0, 63, 68, 0.54)',
                                    flex: 'none',
                                    order: 1,
                                    alignSelf: 'stretch',
                                    flexGrow: 0
                                  }}
                                >
                                  {content.instruction}
                                </Typography>
                              </>
                            )}
                          </Box>
                        </Box>

                        {/* Frame 14614 - Button and Link Container */}
                        <Box sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          padding: '0px',
                          gap: '16px',
                          width: '100%',
                          maxWidth: '342px',
                          flex: 'none',
                          order: 1,
                          alignSelf: 'stretch',
                          flexGrow: 0
                        }}>
                          {/* CTA main - Open Inbox Button - Only show if not rate limited */}
                          {/* {!isRateLimited && (
                            <UIButton
                              variant="contained"
                              size="large"
                              onClick={handleOpenInbox}
                              startIcon={
                                <Box sx={{
                                  width: '24px',
                                  height: '24px',
                                  flex: 'none',
                                  order: 0,
                                  flexGrow: 0
                                }}>
                                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="rgba(255, 255, 255, 0.7)" strokeWidth="2" fill="none" />
                                    <polyline points="22,6 12,13 2,6" stroke="rgba(255, 255, 255, 0.7)" strokeWidth="2" fill="none" />
                                  </svg>
                                </Box>
                              }
                              sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'center',
                                alignItems: 'center',
                                padding: '12px 0px',
                                gap: '5px',
                                width: '100%',
                                maxWidth: '342px',
                                height: '48px',
                                background: '#00717A',
                                borderRadius: '10px',
                                flex: 'none',
                                order: 0,
                                alignSelf: 'stretch',
                                flexGrow: 0,
                                fontFamily: 'Roboto',
                                fontStyle: 'normal',
                                fontWeight: 600,
                                fontSize: '18px',
                                lineHeight: '20px',
                                color: '#FFFFFF',
                                textTransform: 'none',
                                textDecoration: 'none',
                                boxShadow: 'none',
                                '&:hover': {
                                  backgroundColor: '#005a61',
                                  boxShadow: 'none'
                                }
                              }}
                            >
                              Open inbox
                            </UIButton>
                          )} */}

                          {/* Frame 14611 - Resend actions - Show based on attempts */}
                          {!isRateLimited && (
                            resendAttempts === 0 ? (
                              showResendOption ? (
                                <Box sx={{
                                  display: 'flex',
                                  flexDirection: 'row',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '100%'
                                }}>
                                  <Typography sx={{
                                    width: '100%',
                                    fontFamily: 'Roboto',
                                    fontStyle: 'normal',
                                    fontWeight: 400,
                                    fontSize: '16px',
                                    lineHeight: '19px',
                                    textAlign: 'center',
                                    color: 'rgba(0, 0, 0, 0.54)'
                                  }}>
                                    <>
                                      Didn't receive the email?{' '}
                                      <Link
                                        onClick={handleResendMagicLink}
                                        sx={{
                                          color: '#2563EB',
                                          textDecoration: 'underline',
                                          cursor: 'pointer',
                                          fontWeight: 500
                                        }}
                                      >
                                        Resend
                                      </Link>
                                      .
                                    </>
                                  </Typography>
                                </Box>
                              ) : (
                                <Box sx={{
                                  display: 'flex',
                                  flexDirection: 'row',
                                  alignItems: 'flex-start',
                                  padding: '0px',
                                  gap: '10px',
                                  width: '100%',
                                  maxWidth: '247px',
                                  flex: 'none',
                                  order: 1,
                                  flexGrow: 0
                                }}>
                                  <Typography sx={{
                                    width: '100%',
                                    fontFamily: 'Roboto',
                                    fontStyle: 'normal',
                                    fontWeight: 400,
                                    fontSize: '16px',
                                    lineHeight: '19px',
                                    textAlign: 'center',
                                    color: 'rgba(0, 0, 0, 0.54)',
                                    flex: 'none',
                                    order: 0,
                                    flexGrow: 0
                                  }}>
                                    {resendTimer > 0 ? (
                                      <>Didn't receive the email? Try again in {formatTime(resendTimer)}</>
                                    ) : (
                                      <>Didn't receive the email? Please wait...</>
                                    )}
                                  </Typography>
                                </Box>
                              )
                            ) : (
                              <Box sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'flex-start',
                                padding: '0px',
                                gap: '10px',
                                width: '100%',
                                maxWidth: '247px',
                                flex: 'none',
                                order: 1,
                                flexGrow: 0
                              }}>
                                <Typography sx={{
                                  width: '100%',
                                  fontFamily: 'Roboto',
                                  fontStyle: 'normal',
                                  fontWeight: 400,
                                  fontSize: '16px',
                                  lineHeight: '19px',
                                  textAlign: 'center',
                                  color: 'rgba(0, 0, 0, 0.54)',
                                  flex: 'none',
                                  order: 0,
                                  flexGrow: 0
                                }}>
                                  {showResendOption ? (
                                    <>
                                      Didn't receive the email?{' '}
                                      <Link
                                        onClick={handleResendMagicLink}
                                        sx={{
                                          color: '#2563EB',
                                          textDecoration: 'underline',
                                          cursor: 'pointer',
                                          fontWeight: 500
                                        }}
                                      >
                                        Try again
                                      </Link>
                                      .
                                    </>
                                  ) : resendTimer > 0 ? (
                                    <>
                                      Didn't receive the email? Try again in {formatTime(resendTimer)}
                                    </>
                                  ) : (
                                    <>
                                      Didn't receive the email? Please wait...
                                    </>
                                  )}
                                </Typography>
                              </Box>
                            )
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </>
              );
            })()
          ) : (
            <Box sx={{ p: 3, width: '100%' }}>
              {/* Header for Login/Signup Form */}
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2
              }}>
                <Typography variant="h5" sx={{
                  fontWeight: 700,
                  fontSize: '20px',
                  color: linkExpired ? '#E60000' : '#000000',
                  fontFamily: 'Roboto'
                }}>
                  {linkExpired ? 'Link Expired' : 'Welcome to Utilities Plus'}
                </Typography>
                <IconButton
                  onClick={handleCloseBottomSheet}
                  sx={{
                    p: 1,
                    color: '#000000'
                  }}
                >
                  <CloseIcon sx={{ fontSize: '18px' }} />
                </IconButton>
              </Box>
              <Divider />

              <Stack sx={{ mt: 4 }}>
                {/* Email Field */}
                <Box sx={{ mb: 0 }}>
                  <TextField
                    label="Email address"
                    value={formData.email}
                    onChange={handleInputChange('email')}
                    onBlur={handleEmailBlur}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !signupFlowActive && !loading.magicLink) {
                        e.preventDefault();
                        handleSendMagicLink();
                      }
                    }}
                    placeholder="E.g. example@email.com"
                    helperTop={
                      linkExpired
                        ? 'This link has expired. Please enter your email address and request a new verification link.'
                        : "Enter the email you use most. We'll use this to sign you in and send updates."
                    }
                    disabled={signupFlowActive || !!loading.magicLink}
                    error={emailTouched && !!emailError}
                    helperText={emailTouched && emailError ? emailError : ''}
                    deferValidation={true}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '50px',
                        backgroundColor: '#FFFFFF',
                        borderRadius: '8px',
                        '& fieldset': {
                          borderColor: emailTouched && emailError ? '#d32f2f' : '#D1D5DB',
                          borderWidth: '1px'
                        },
                        '&:hover fieldset': {
                          borderColor: emailTouched && emailError ? '#d32f2f' : '#9CA3AF'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: emailTouched && emailError ? '#d32f2f' : '#00717A',
                          borderWidth: '2px'
                        },
                        '&.Mui-error fieldset': {
                          borderColor: '#d32f2f'
                        },
                        '& input': {
                          fontFamily: 'Roboto',
                          fontSize: '16px',
                          color: '#000000',
                          padding: '14px 12px',
                          '&::placeholder': {
                            color: '#9CA3AF',
                            opacity: 1
                          }
                        }
                      }
                    }}
                  />
                </Box>

                {/* Name field removed: auto account creation */}

                {/* Primary Action */}
                {dialogType === 'signup' && signupFlowActive ? (
                  <Typography sx={{
                    mt: '22px',
                    fontSize: '14px',
                    fontWeight: 500,
                    textAlign: 'center',
                    color: '#6B7280',
                    fontFamily: 'Roboto'
                  }}>
                    {loading.createUser ? 'Creating your account...' : 'Preparing your account...'}
                  </Typography>
                ) : (
                  <UIButton
                    variant="contained"
                    size="large"
                    onClick={handleSendMagicLink}
                    disabled={loading.magicLink}
                    sx={{
                      py: 2,
                      height: '50px',
                      backgroundColor: '#00717A',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: 600,
                      textTransform: 'none',
                      boxShadow: 'none',
                      fontFamily: 'Roboto',
                      '&:hover': {
                        backgroundColor: '#005a61',
                        boxShadow: 'none'
                      },
                      '&:disabled': {
                        backgroundColor: '#E5E7EB',
                        color: '#9CA3AF'
                      }
                    }}
                  >
                    {loading.magicLink ? 'Sending...' : 'Continue'}
                  </UIButton>
                )}

                {/* Divider */}
                {!signupFlowActive && <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
                  <Divider sx={{ flex: 1, borderColor: '#E4E7EB' }} />
                  <Typography sx={{
                    px: 2,
                    fontSize: '14px',
                    color: '#6B7280',
                    fontFamily: 'Roboto'
                  }}>
                    Or continue with
                  </Typography>
                  <Divider sx={{ flex: 1, borderColor: '#E4E7EB' }} />
                </Box>}

                {/* Google Button */}
                {!signupFlowActive && <UIButton
                  variant="outlined"
                  size="large"
                  onClick={handleGoogleLogin}
                  loading={loading?.googleAuth}
                  loadingText="Redirecting to Google..."
                  startIcon={
                    <Box sx={{
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                      </svg>
                    </Box>
                  }
                  sx={{
                    py: 2,
                    height: '50px',
                    borderColor: '#D1D5DB',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: 500,
                    textTransform: 'none',
                    color: '#000000',
                    backgroundColor: '#FFFFFF',
                    boxShadow: 'none',
                    fontFamily: 'Roboto',
                    '&:hover': {
                      borderColor: '#9CA3AF',
                      backgroundColor: '#F9FAFB',
                      boxShadow: 'none'
                    }
                  }}
                >
                  Continue with Google
                </UIButton>}



                {/* Terms and Privacy */}
                {!signupFlowActive && <Typography sx={{
                  fontSize: '14px',
                  color: '#6B7280',
                  textAlign: 'center',
                  lineHeight: '22px',
                  fontFamily: 'Roboto',
                  mt: 2,
                  mb: 2
                }}>
                  By continuing you agree to the<br />
                  <Link
                    onClick={() => setTermsOpen(true)}
                    sx={{ color: '#2563EB', textDecoration: 'underline', cursor: 'pointer' }}
                  >
                    {dialogType === 'login' ? 'Terms and Conditions' : 'Terms of Service'}
                  </Link>
                  {' '}and{' '}
                  <Link
                    onClick={() => setPrivacyOpen(true)}
                    sx={{ color: '#2563EB', textDecoration: 'underline', cursor: 'pointer' }}
                  >
                    Privacy Policy
                  </Link>
                </Typography>}

                {/* GDPR Badge */}
                {!signupFlowActive && <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  mb: 2
                }}>
                  <Box
                    component="img"
                    src="/assets/onBoarding/gdpr.svg"
                    alt="GDPR"
                    sx={{
                      width: 45,
                      height: 45
                    }}
                  />
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}>
                    <Typography sx={{
                      fontSize: '14px',
                      color: '#374151',
                      fontWeight: 500,
                      fontFamily: 'Roboto'
                    }}>
                      Secure & Confidential
                    </Typography>
                  </Box>
                </Box>}
              </Stack>
            </Box>
          )}
        </Box>
      </Drawer>

      {/* Help dialog is mounted globally via HelpProvider */}

      {/* Terms and Privacy Drawers */}
      <InfoDrawer open={termsOpen} onClose={() => setTermsOpen(false)} content={termsContent} />
      <InfoDrawer open={privacyOpen} onClose={() => setPrivacyOpen(false)} content={privacyContent} />

      {/* Error Bottom Sheet - Mobile slide-up, consistent with magic link screens */}
      <Drawer
        anchor="bottom"
        open={errorSheetOpen && Boolean(verifyError)}
        onClose={() => {
          setErrorSheetOpen(false);
          if (verifyError) setVerifyError(null);
          setShowLoginOptions(true);
          setLoginBottomSheetOpen(false);
          resetToLogin();
          try { navigate('/auth', { replace: true }); } catch { }
        }}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            background: '#FFFFFF',
            borderRadius: '12px 12px 0px 0px',
            maxHeight: '85vh',
            minHeight: '65vh',
            height: 'auto',
            overflow: 'hidden',
            padding: 0,
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            // iOS Safari: force GPU compositing to avoid white flashes
            transform: 'translateZ(0)',
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            willChange: 'transform'
          }
        }}
      >
        <Box key={contentEpoch} sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          {/* Header with close */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2, borderBottom: '1px solid #E5E7EB' }}>
            <IconButton
              onClick={() => {
                setErrorSheetOpen(false);
                if (verifyError) setVerifyError(null);
                setShowLoginOptions(true);
                setLoginBottomSheetOpen(false);
                resetToLogin();
                try { navigate('/auth', { replace: true }); } catch { }
              }}
              sx={{ p: 0.5, color: '#000000' }}
            >
              <CloseIcon sx={{ fontSize: '18px' }} />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ px: 3, py: 3, textAlign: 'center' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Box
                component="img"
                src={verifyError?.message === 'Account Locked' ? '/assets/onBoarding/postcodeBlocked.png' : '/assets/onBoarding/MagicLink.svg'}
                alt={verifyError?.message === 'Account Locked' ? 'Account Locked' : 'Verification Error'}
                sx={{ width: 300, maxWidth: '100%', height: 'auto' }}
              />
            </Box>
            <Typography sx={{ fontFamily: 'Roboto', fontWeight: 700, fontSize: '22px', color: verifyError?.message === 'Account Locked' ? '#003F44' : '#155F54', mb: 1.5 }}>
              {verifyError?.message === 'Account Locked' ? 'Your Account Has Been Locked' : 'Magic link expired'}
            </Typography>
            <Typography sx={{ fontFamily: 'Roboto', fontWeight: 600, fontSize: '14px', color: '#d32f2f', mb: 2.5 }}>
              {verifyError?.message === 'Account Locked' ? 'Postcode Verification Failed' : ''}
            </Typography>
            <Typography sx={{ fontFamily: 'Roboto', fontWeight: 400, fontSize: '14px', color: 'rgba(49, 49, 49, 0.54)', mb: 2.5 }}>
              {verifyError?.description || (linkExpired ? 'Your magic link has expired. Please return to login and request a new link.' : 'Please try again later or contact support.')}
            </Typography>

            {/* Show Return to Login button for expired links, Contact Support for other errors */}
            {linkExpired || (!verifyError?.message || verifyError?.message !== 'Account Locked') ? (
              <UIButton
                variant="contained"
                size="large"
                onClick={() => {
                  setErrorSheetOpen(false);
                  if (verifyError) setVerifyError(null);
                  setShowLoginOptions(true);
                  setLoginBottomSheetOpen(false);
                  resetToLogin();
                  try { navigate('/auth', { replace: true }); } catch { }
                }}
                sx={{
                  width: '100%',
                  maxWidth: '280px',
                  height: '48px',
                  backgroundColor: '#00717A',
                  borderRadius: '10px',
                  fontFamily: 'Roboto',
                  fontWeight: 600,
                  fontSize: '16px',
                  lineHeight: '20px',
                  color: '#FFFFFF',
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: '#005a61',
                    boxShadow: 'none'
                  }
                }}
              >
                Return to login
              </UIButton>
            ) : (
              <Typography
                component="a"
                href="mailto:customer.support@iehub.co.uk"
                sx={{
                  fontFamily: 'Roboto',
                  fontWeight: 400,
                  fontSize: '14px',
                  color: '#4A6B70',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  '&:hover': {
                    color: '#2d4a4e'
                  }
                }}
              >
                Please Contact Support for help
              </Typography>
            )}
          </Box>
        </Box>
      </Drawer>

      {/* Desktop Error Dialog */}
      <Dialog
        open={isDesktop && (errorSheetOpen && Boolean(verifyError))}
        onClose={() => {
          setErrorSheetOpen(false);
          if (verifyError) setVerifyError(null);
          setShowLoginOptions(true);
          setLoginBottomSheetOpen(false);
          resetToLogin();
          try { navigate('/auth', { replace: true }); } catch { }
        }}
        PaperProps={{
          sx: {
            display: { xs: 'none', md: 'block' },
            borderRadius: '12px',
            width: '100%',
            maxWidth: 480
          }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2, borderBottom: '1px solid #E5E7EB' }}>
            <IconButton
              onClick={() => {
                setErrorSheetOpen(false);
                if (verifyError) setVerifyError(null);
                setShowLoginOptions(true);
                setLoginBottomSheetOpen(false);
                resetToLogin();
                try { navigate('/auth', { replace: true }); } catch { }
              }}
              sx={{ p: 0.5, color: '#000000' }}
            >
              <CloseIcon sx={{ fontSize: '18px' }} />
            </IconButton>
          </Box>
          <Box sx={{ px: 3, py: 3, textAlign: 'center' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Box
                component="img"
                src={verifyError?.message === 'Account Locked' ? '/assets/onBoarding/postcodeBlocked.png' : '/assets/onBoarding/MagicLink.svg'}
                alt={verifyError?.message === 'Account Locked' ? 'Account Locked' : 'Verification Error'}
                sx={{ width: 180, maxWidth: '100%', height: 'auto' }}
              />
            </Box>
            <Typography sx={{ fontFamily: 'Roboto', fontWeight: 700, fontSize: '22px', color: verifyError?.message === 'Account Locked' ? '#003F44' : '#155F54', mb: 1.5 }}>
              {verifyError?.message === 'Account Locked' ? 'Your Account Has Been Locked' : 'Magic link expired'}
            </Typography>
            <Typography sx={{ fontFamily: 'Roboto', fontWeight: 600, fontSize: '14px', color: '#d32f2f', mb: 1.5 }}>
              {verifyError?.message === 'Account Locked' ? 'Postcode Verification Failed' : ''}
            </Typography>
            <Typography sx={{ fontFamily: 'Roboto', fontWeight: 400, fontSize: '14px', color: 'rgba(49, 49, 49, 0.54)', mb: 2.5 }}>
              {verifyError?.description || (linkExpired ? 'Your magic link has expired. Please return to login and request a new link.' : 'Please try again later or contact support.')}
            </Typography>

            {/* Show Return to Login button for expired links, Contact Support for other errors */}
            {linkExpired || (!verifyError?.message || verifyError?.message !== 'Account Locked') ? (
              <UIButton
                variant="contained"
                size="large"
                onClick={() => {
                  setErrorSheetOpen(false);
                  if (verifyError) setVerifyError(null);
                  setShowLoginOptions(true);
                  setLoginBottomSheetOpen(false);
                  resetToLogin();
                  try { navigate('/auth', { replace: true }); } catch { }
                }}
                sx={{
                  width: '100%',
                  maxWidth: '240px',
                  height: '44px',
                  backgroundColor: '#00717A',
                  borderRadius: '8px',
                  fontFamily: 'Roboto',
                  fontWeight: 600,
                  fontSize: '16px',
                  lineHeight: '20px',
                  color: '#FFFFFF',
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: '#005a61',
                    boxShadow: 'none'
                  }
                }}
              >
                Return to login
              </UIButton>
            ) : (
              <Typography
                component="a"
                href="mailto:customer.support@iehub.co.uk"
                sx={{
                  fontFamily: 'Roboto',
                  fontWeight: 400,
                  fontSize: '14px',
                  color: '#4A6B70',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  '&:hover': {
                    color: '#2d4a4e'
                  }
                }}
              >
                Please Contact Support for help
              </Typography>
            )}
          </Box>
        </Box>
      </Dialog>

      {/* Mail app chooser dialog - Desktop only */}
      <Dialog
        open={mailChooserOpen}
        onClose={() => setMailChooserOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            minWidth: '320px'
          }
        }}
      >
        <DialogTitle sx={{
          fontFamily: 'Roboto',
          fontWeight: 600,
          fontSize: '18px',
          pb: 1
        }}>
          Select your mail app
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <List sx={{ py: 0 }}>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => openProvider('gmail')}
                sx={{
                  py: 1.5,
                  '&:hover': { bgcolor: 'rgba(0, 113, 122, 0.08)' }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Box sx={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L12 9.545l8.073-6.052C21.69 2.28 24 3.434 24 5.457z" fill="#EA4335" />
                    </svg>
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary="Gmail"
                  primaryTypographyProps={{
                    fontFamily: 'Roboto',
                    fontSize: '15px',
                    fontWeight: 500
                  }}
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => openProvider('outlook')}
                sx={{
                  py: 1.5,
                  '&:hover': { bgcolor: 'rgba(0, 113, 122, 0.08)' }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Box sx={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M24 7.386v9.228c0 .84-.68 1.52-1.52 1.52h-8.788a6.97 6.97 0 0 1-2.575-.49l-.03-.012a6.978 6.978 0 0 1-4.11-6.35v-.564a6.978 6.978 0 0 1 4.11-6.35l.03-.012a6.97 6.97 0 0 1 2.576-.49h8.788c.84 0 1.52.68 1.52 1.52z" fill="#0078D4" />
                      <path d="M13.692 4.866h-2.605a6.978 6.978 0 0 0-4.11 6.35v.564a6.978 6.978 0 0 0 4.11 6.35h2.605V4.866z" fill="#0364B8" />
                      <path d="M6.977 4.866H1.52C.68 4.866 0 5.546 0 6.386v9.228c0 .84.68 1.52 1.52 1.52h5.457V4.866z" fill="#0078D4" />
                      <path d="M6.977 10.718v2.564H1.52v-2.564h5.457z" fill="#28A8EA" />
                      <path d="M13.692 10.718v2.564h-6.715v-2.564h6.715z" fill="#0078D4" />
                      <path d="M13.692 10.718v2.564H11.087a6.978 6.978 0 0 1 0-2.564h2.605z" fill="#0364B8" />
                    </svg>
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary="Outlook / Live"
                  primaryTypographyProps={{
                    fontFamily: 'Roboto',
                    fontSize: '15px',
                    fontWeight: 500
                  }}
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => openProvider('yahoo')}
                sx={{
                  py: 1.5,
                  '&:hover': { bgcolor: 'rgba(0, 113, 122, 0.08)' }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Box sx={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0z" fill="#6001D2" />
                      <path d="M7.234 7.234h2.532l-2.532 4.876v4.656H5.766V12.11l-2.532-4.876h2.532L7.234 11.2l1.468-3.966zM14.016 16.766h-1.5v-.984c-.36.72-1.044 1.14-2.004 1.14-1.56 0-2.628-1.248-2.628-3.168 0-1.92 1.068-3.156 2.628-3.156.96 0 1.644.408 2.004 1.128v-.984h1.5v6.024zm-3.024-4.8c-.828 0-1.392.636-1.392 1.788 0 1.152.564 1.8 1.392 1.8.828 0 1.392-.648 1.392-1.8 0-1.152-.564-1.788-1.392-1.788zM18.234 16.766h-1.5v-2.652c0-.96-.348-1.428-1.056-1.428-.72 0-1.188.54-1.188 1.428v2.652h-1.5v-8.532h1.5v3.684c.396-.6 1.008-.96 1.908-.96 1.32 0 2.136.924 2.136 2.472v3.336z" fill="#FFFFFF" />
                    </svg>
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary="Yahoo Mail"
                  primaryTypographyProps={{
                    fontFamily: 'Roboto',
                    fontSize: '15px',
                    fontWeight: 500
                  }}
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => openProvider('icloud')}
                sx={{
                  py: 1.5,
                  '&:hover': { bgcolor: 'rgba(0, 113, 122, 0.08)' }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Box sx={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0z" fill="#3B9AE1" />
                      <path d="M12.156 6.456c.636 0 1.44-.42 1.908-1.02.42-.552.732-1.32.732-2.088 0-.108-.012-.216-.024-.276-.696.024-1.536.468-2.04 1.056-.396.456-.768 1.188-.768 1.956 0 .12.024.228.036.264.06.012.156.012.156.012v.096zm.252 1.548c-1.116 0-2.004.672-2.52.672-.54 0-1.368-.648-2.256-.648-1.644 0-3.168 1.02-3.984 2.604-1.716 2.976-.456 7.392 1.224 9.816.828 1.188 1.812 2.52 3.12 2.52 1.044 0 1.44-.672 2.688-.672 1.26 0 1.536.648 2.604.648 1.296 0 2.208-1.26 3.036-2.448.936-1.38 1.32-2.724 1.344-2.796-.084-.024-2.604-1.008-2.604-3.78 0-2.376 1.92-3.528 2.004-3.6-1.224-1.752-3.12-1.944-3.732-1.98-.024 0-.06-.012-.084-.012-.324-.036-.84-.084-1.44-.084z" fill="#FFFFFF" />
                    </svg>
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary="iCloud Mail"
                  primaryTypographyProps={{
                    fontFamily: 'Roboto',
                    fontSize: '15px',
                    fontWeight: 500
                  }}
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                onClick={openDefaultMailApp}
                sx={{
                  py: 1.5,
                  '&:hover': { bgcolor: 'rgba(0, 113, 122, 0.08)' }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Box sx={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect width="24" height="24" rx="4" fill="#00717A" />
                      <path d="M4 7h16c1.1 0 2 .9 2 2v6c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V9c0-1.1.9-2 2-2z" stroke="#FFFFFF" strokeWidth="1.5" fill="none" />
                      <polyline points="20,9 12,13 4,9" stroke="#FFFFFF" strokeWidth="1.5" fill="none" />
                    </svg>
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary="Open default mail app"
                  primaryTypographyProps={{
                    fontFamily: 'Roboto',
                    fontSize: '15px',
                    fontWeight: 500
                  }}
                />
              </ListItemButton>
            </ListItem>
          </List>
        </DialogContent>
      </Dialog>

    </>
  );
}


